import FilterOutlineIcon from '@eduzz/houston-icons/FilterOutline';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import { Form as FormikForm, Field, useFormikContext } from 'formik';
import moment from 'moment';
import React, { useRef } from 'react';
import { isInclusivelyBeforeDay } from 'react-dates';
import { useObservable } from 'react-use-observable';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { SelectProduct } from 'v2/components/Shared/FormFields/SelectProduct';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import useGetUser from 'v2/hooks/useGetUser';
import { EDateTypes } from 'v2/interfaces/reports/refunds';
import { initialFilter, refundsService } from 'v2/services/reports/refunds';
import { refundRequestFactory } from 'v2/services/shopping/refundRequest';

import type { ISelectProductMultipleHandles } from 'v2/components/Shared/FormFields/SelectProduct';
import type { IRefundFilter } from 'v2/interfaces/reports/refunds';

import { useStyles } from './styles';

import type { FieldProps } from 'formik';
import type { Moment } from 'moment';

interface IMotive {
  id?: string;
  description: string;
}

const dateTypes = [
  { label: 'Pagamento', value: EDateTypes.PAID },
  { label: 'Reembolso', value: EDateTypes.REFUNDED },
];

export const Form = () => {
  const classes = useStyles();
  const user = useGetUser();

  const [motives, motivesError] = useObservable<IMotive[]>(() => refundRequestFactory.getRefundMotivesFromBoApi(user?.id), []);

  const selectProductRef = useRef<ISelectProductMultipleHandles>(null);

  const { isValid, isSubmitting, resetForm, dirty } = useFormikContext<IRefundFilter>();

  const [isLoading] = useObservable(() => refundsService.isLoading(), []);

  const isOutsideRange = (day: Moment) => !isInclusivelyBeforeDay(day, moment());

  const handleClean = () => {
    resetForm({ values: initialFilter });
    selectProductRef.current?.unCheckAll();
  };

  const motivesOptions = (motives || []).map((item) => ({
    label: item.description,
    value: item.id,
  }));

  const loadingMotivesOptions = motivesError === null && motives === null;
  const isCleanDisabled = !dirty || isSubmitting || isLoading;
  const isFilterDisabled = !isValid || isSubmitting || isLoading;

  return (
    <FormikForm>
      <div id='refunds-filter' className={classes.root}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6} xl={4}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={4}>
                <Field
                  id='date_type'
                  name='date_type'
                  label='Tipo de data'
                  inputProps={{
                    className: classes.leftInput,
                  }}
                  options={dateTypes}
                  component={SelectFormField}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={8}>
                <Field id='date' label='Período' name='date' component={DateRangePickerFormField} isPresetLonger isOutsideRange={isOutsideRange} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6} xl={2}>
            <Field id='product_id' name='product_id'>
              {(props: FieldProps) => <SelectProduct {...props} label='Produtos' endpoint='/reports/sales-filter-products' ref={selectProductRef} />}
            </Field>
          </Grid>
          <Grid item xs={12} sm={4} md={2} xl={2}>
            <Field id='invoice_id' name='invoice_id' label='Fatura' mask='number' component={TextFormField} fullWidth />
          </Grid>
          <Grid item xs={12} sm={8} md={4} xl={2}>
            <Field
              id='motive_id'
              name='motive_id'
              label='Motivo'
              loading={loadingMotivesOptions}
              options={motivesOptions}
              component={SelectFormField}
              multiple
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} xl={1} className={classes.buttonContainer}>
            <Button id='clean-filter-button' onClick={handleClean} variant='text' disabled={isCleanDisabled} fullWidth>
              Limpar
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={1} className={classes.buttonContainer}>
            <Button
              id='filter-button'
              color='primary'
              variant='contained'
              disabled={isFilterDisabled}
              type='submit'
              startIcon={
                isSubmitting || isLoading ? <CircularProgress size={20} color='inherit' /> : <FilterOutlineIcon className={classes.filterIcon} />
              }
              fullWidth
            >
              Filtrar
            </Button>
          </Grid>
        </Grid>
      </div>
    </FormikForm>
  );
};
